import { RuleCreationTypeEnum } from '@pypestream/api-services';
import {
  Button,
  Card,
  Icon,
  IconButton,
  Input,
  PageSection,
  Spacer,
  Stack,
  Tag,
  TextBody,
  TextTitle,
  Tooltip,
} from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import { FC, useEffect, useMemo, useRef, useState } from 'react';

import './data-privacy.scss';

import { MaskingPatternText } from '../../components/masking-pattern-text';
import { useDeleteRuleSetModal } from '../../components/modals';
import { SettingsEmptyState } from '../../components/settings-empty-state';
import { SettingsSectionLoader } from '../../components/settings-section-loader';
import { useLoadingState, useProStudioViewerRole } from '../../hooks';
import {
  Channel,
  checkMaskingPattern,
  containsInvisibleCharacters,
  decideRuleSetRename,
  getEnvDotColor,
  getPatternDisplayProps,
  getRuleNameDisplayProps,
  isDefaultRuleSet,
  isUsableRuleName,
  MaskingRule,
  passesCompileCheck,
  RuleSet,
  toPlatformChannel,
} from '../../utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';
import { useGoToChannel } from './use-go-to-channel';

const isSystemRule = (rule: MaskingRule) =>
  rule.type === RuleCreationTypeEnum.System;

// The legacy grey redirect tag pill, the same one the Reporting API
// Credentials column draws (JSX needs a style object, not the Lit-template
// style string). The Live Agent Platforms "Channels Using It" column no longer
// uses it: since PSTUDIO-4343 it renders solution boxes instead.
const PILL_STYLE = {
  ['--ps-tag-bg-color' as string]: 'rgba(98, 105, 124, 0.05)',
  ['--ps-tag-bg-hover-color' as string]: 'rgba(98, 105, 124, 0.1)',
};

/** A channel pill that deep-links to its channel-settings page. */
const ChannelPill: FC<{ channel: Channel; onOpen: () => void }> = ({
  channel,
  onOpen,
}) => (
  <Tooltip content="Go to channel">
    <Tag
      test-id="ps-data-privacy-channel-pill"
      size="small"
      minimal
      interactive
      style={PILL_STYLE}
      // `onClick` is missing from this wrapper's props type; React binds it
      // regardless, so spread it to sidestep the excess-property check.
      {...{ onClick: onOpen }}
    >
      <span
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          flex: '0 0 auto',
          background: getEnvDotColor(channel.env),
        }}
      />
      {channel.name}
      <Icon name="arrow-in-circle" size="xsmall" />
    </Tag>
  </Tooltip>
);

/** "Channels Using It:" — channels whose pype+env is assigned to a rule set. */
const ChannelsUsingIt: FC<{ channels: Channel[] }> = ({ channels }) => {
  const goToChannel = useGoToChannel();

  if (!channels.length) {
    return null;
  }

  return (
    <>
      <Spacer size="medium" />
      <Card size="medium" border="default" shadow="none">
        <TextBody size="small" variant="secondary">
          Channels Using It:
        </TextBody>
        <Spacer size="xsmall" />
        <Stack gutter="xsmall" style={{ flexWrap: 'wrap' }}>
          {channels.map((channel) => (
            <ChannelPill
              key={`${channel.pypeId}-${channel.id}`}
              channel={channel}
              onOpen={() =>
                goToChannel(
                  toPlatformChannel({
                    name: channel.name,
                    id: channel.id,
                    botId: channel.botId,
                    type: channel.type,
                  })
                )
              }
            />
          ))}
        </Stack>
      </Card>
    </>
  );
};

/**
 * Edit view for a single masking rule (name + its regex). Handles both editing
 * an existing rule and creating a new one within a rule set. All saves go
 * through the group-level `updatePypeRuleGroup` event.
 *
 * A rule holds exactly ONE regex: PSTUDIO-3804 dropped the "rule pattern"
 * sub-layer, so there is no list to add to and no named sub-patterns — a rule
 * set contains rules, and a rule contains a pattern.
 */
const RuleEditor: FC<{
  groupId: string;
  groupName: string;
  rule?: MaskingRule;
  onClose: () => void;
}> = ({ groupId, groupName, rule, onClose }) => {
  const [t] = useTranslation();
  const isEdit = Boolean(rule?.id);
  const [name, setName] = useState(rule?.name ?? '');
  const [pattern, setPattern] = useState(rule?.patterns[0] ?? '');
  // Memoised on the pattern alone, and that is load-bearing rather than tidy:
  // the complexity check RUNS the candidate regex, which costs a few hundred
  // milliseconds for a catastrophic one (PSTUDIO-4300). Without this, typing in
  // the Name field would re-pay that cost on every keystroke.
  const patternValidity = useMemo(
    () => checkMaskingPattern(pattern),
    [pattern]
  );

  // Three states, deliberately not one. A pattern that was typed and either
  // cannot compile or cannot run in reasonable time is an error: red field, red
  // message, and the two messages differ because the remedies differ. An empty
  // pattern also blocks Save — a rule without one masks nothing — but it reads
  // as a prompt, not a failure: both the add form and the edit form of a rule
  // that holds no patterns open empty, and a field that is red before it has
  // been touched reads as the user's mistake.
  //
  // Either way the reason is stated, and that half is not cosmetic. Save is
  // disabled here where it was not before, so an existing rule with an empty
  // `patterns` array can no longer be renamed without also giving it a pattern —
  // without a message, that is a dead button and no explanation.
  const showPatternError =
    patternValidity.passedPresenceCheck &&
    !(
      patternValidity.passedCharactersCheck &&
      patternValidity.passedCompileCheck &&
      patternValidity.passedComplexityCheck
    );
  let patternHelpText = '';
  if (!patternValidity.passedPresenceCheck) {
    patternHelpText = String(
      t('pro-studio/common:maskingRules.patternRequired')
    );
  } else if (!patternValidity.passedCharactersCheck) {
    patternHelpText = String(
      t('pro-studio/common:maskingRules.invisibleCharacters')
    );
  } else if (!patternValidity.passedCompileCheck) {
    patternHelpText = String(
      t('pro-studio/common:maskingRules.invalidPattern')
    );
  } else if (!patternValidity.passedComplexityCheck) {
    patternHelpText = String(
      t('pro-studio/common:maskingRules.patternTooSlow')
    );
  }
  // The name is judged on the same terms as the pattern: `trim()` leaves a
  // zero-width space in place, so a name made of one would otherwise pass as
  // non-empty and be saved as a rule that renders blank (PSTUDIO-4123, S10).
  const nameHasInvisibleCharacters = containsInvisibleCharacters(name.trim());
  const canSave = isUsableRuleName(name) && patternValidity.valid;
  // Rules created before the single-pattern change may hold several patterns.
  // Saving here writes back exactly one, so say so rather than dropping the
  // extras silently — they decide what the rule actually masks today.
  const dropsExtraPatterns = (rule?.patterns.length ?? 0) > 1;

  const handleSave = () => {
    const cleanedPatterns = [pattern.trim()].filter(Boolean);

    sendProStudioEvent({
      type: 'updatePypeRuleGroup',
      payload: {
        id: groupId,
        name: groupName,
        ...(isEdit && rule
          ? {
              rulesToUpdate: [
                { id: rule.id, name: name.trim(), rules: cleanedPatterns },
              ],
            }
          : {
              rulesToAdd: [
                {
                  name: name.trim(),
                  type: RuleCreationTypeEnum.ManuallyCreated,
                  rules: cleanedPatterns,
                },
              ],
            }),
        cb: onClose,
      },
    });
  };

  const handleDelete = () => {
    if (!rule?.id) {
      return;
    }
    sendProStudioEvent({
      type: 'updatePypeRuleGroup',
      payload: {
        id: groupId,
        name: groupName,
        rulesToDelete: [rule.id],
        cb: onClose,
      },
    });
  };

  return (
    <Card size="medium" border="default" shadow="none">
      <Stack alignItems="center" justifyContent="space-between" nowrap>
        {/* `minWidth: 0` + `flex: 1 1 0` let the name shrink and clamp; the
            actions on the right keep their natural width (`flex: 0 0 auto`). */}
        <Stack
          alignItems="center"
          gutter="xsmall"
          nowrap
          style={{ minWidth: 0, flex: '1 1 0' }}
        >
          <TextBody
            {...getRuleNameDisplayProps(isEdit ? (rule?.name ?? '') : '')}
          >
            {isEdit ? rule?.name : 'New Rule'}
          </TextBody>
          {isEdit && (
            <IconButton name="delete" size="small" onClick={handleDelete} />
          )}
        </Stack>
        <Stack gutter="xsmall" nowrap style={{ flex: '0 0 auto' }}>
          <Button size="small" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button size="small" disabled={!canSave} onClick={handleSave}>
            Save
          </Button>
        </Stack>
      </Stack>

      <Spacer size="medium" />
      <TextBody size="small">Rule Name</TextBody>
      <Spacer size="2xsmall" />
      <Input
        variant="outlined"
        value={name}
        hasError={nameHasInvisibleCharacters}
        helpText={
          nameHasInvisibleCharacters
            ? String(t('pro-studio/common:maskingRules.invisibleCharacters'))
            : ''
        }
        onChange={(e) => setName((e.target as HTMLInputElement).value)}
        onInput={(e) => setName((e.target as HTMLInputElement).value)}
      />

      <Spacer size="medium" />
      <TextBody size="small">Rule Pattern</TextBody>
      <Spacer size="2xsmall" />
      <Input
        test-id="ps-data-privacy-rule-pattern-input"
        {...getPatternDisplayProps()}
        variant="outlined"
        value={pattern}
        hasError={showPatternError}
        helpText={patternHelpText}
        onChange={(e) => setPattern((e.target as HTMLInputElement).value)}
        onInput={(e) => setPattern((e.target as HTMLInputElement).value)}
      />
      {dropsExtraPatterns && (
        <>
          <Spacer size="2xsmall" />
          <TextBody
            test-id="ps-data-privacy-rule-extra-patterns-warning"
            size="small"
            variant="secondary"
          >
            This rule still holds {rule?.patterns.length} patterns. Saving keeps
            only the one above; the rest stop masking.
          </TextBody>
        </>
      )}
    </Card>
  );
};

const RuleRow: FC<{
  groupId: string;
  groupName: string;
  rule: MaskingRule;
}> = ({ groupId, groupName, rule }) => {
  const [t] = useTranslation();
  const [editing, setEditing] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const isViewer = useProStudioViewerRole();
  const system = isSystemRule(rule);
  const hasPatterns = rule.patterns.length > 0;
  const toggleExpanded = () => setExpanded((prev) => !prev);

  // PSTUDIO-4301. The Save gate only ever sees patterns being written; every rule
  // stored before it existed keeps whatever it was given, and renders here
  // exactly like a working one. Marking it is the whole point: the widget skips
  // an uncompilable rule silently, so the list is the only place a user can learn
  // that a rule they believe protects PII masks nothing.
  //
  // The COMPILE check only, deliberately — `checkMaskingPattern` would also run
  // the complexity probe, which EXECUTES the regex, and this component renders
  // once per rule of every set. A slow-but-valid pattern is refused where it is
  // authored; here the cost would be paid on every render of the page.
  const brokenPatterns = useMemo(
    () => rule.patterns.filter((pattern) => !passesCompileCheck(pattern)),
    [rule.patterns]
  );

  // Collapsed row — stays visible while editing; the editor renders as a
  // separate block below it (not in place). Highlights with a purple border on
  // hover.
  //
  // The whole row is the toggle for the pattern (PSTUDIO-3804): the dedicated
  // Show/Hide control stays, but hitting it is no longer the only way in.
  //
  // Deliberately a bare click target — no `role="button"`/`tabIndex` on the
  // wrapper. A button's children are presentational, so giving the row that role
  // would swallow the Show/Hide and Edit buttons nested inside it and leave
  // screen-reader users with no way to edit a rule. Keyboard and AT reach the
  // same toggle through the real Show/Hide button; this is a pointer shortcut.
  //
  // It also sits on a plain wrapper rather than on <Card>: Card is a web
  // component wrapper, so handlers it doesn't declare are set as properties and
  // quietly never fire (the same reason RuleSetName wraps its title in a span).
  const row = (
    <div
      {...(hasPatterns && {
        style: { cursor: 'pointer' },
        onClick: toggleExpanded,
      })}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Card
        test-id={`ps-data-privacy-rule-row-${rule.id}`}
        size="medium"
        border={hovered ? 'primary' : 'default'}
        shadow="none"
        style={{ width: '100%' }}
      >
        <Stack alignItems="center" justifyContent="space-between" nowrap>
          {/* Same split as the editor header: the name side shrinks and clamps,
              the action side keeps its width so Show/Edit stay on screen. */}
          <Stack
            alignItems="center"
            gutter="xsmall"
            nowrap
            style={{ minWidth: 0, flex: '1 1 0' }}
          >
            <TextBody {...getRuleNameDisplayProps(rule.name)}>
              {rule.name}
            </TextBody>
            {system && (
              <Tag size="small" minimal>
                System Rule
              </Tag>
            )}
            {brokenPatterns.length > 0 && (
              <Tag
                test-id={`ps-data-privacy-rule-broken-${rule.id}`}
                size="small"
                minimal
                variant="error"
              >
                {String(t('pro-studio/common:maskingRules.brokenPatternTag'))}
              </Tag>
            )}
          </Stack>
          <Stack
            alignItems="center"
            gutter="xsmall"
            nowrap
            style={{ flex: '0 0 auto' }}
          >
            {hasPatterns && (
              <Button
                test-id={`ps-data-privacy-rule-toggle-${rule.id}`}
                size="small"
                variant="ghost"
                // Without this the row's own handler runs too and the two
                // toggles cancel out, leaving the panel unchanged.
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded();
                }}
              >
                <Icon
                  name={expanded ? 'chevrons-down-up' : 'chevrons-up-down'}
                  size="small"
                  slot="prefix"
                />
                {expanded ? 'Hide pattern' : 'Show pattern'}
              </Button>
            )}
            <Button
              size="small"
              variant="secondary"
              disabled={system || editing || isViewer}
              onClick={(e) => {
                e.stopPropagation();
                setEditing(true);
              }}
            >
              <Icon
                name={system ? 'lock' : 'pencil'}
                size="small"
                slot="prefix"
              />
              Edit
            </Button>
          </Stack>
        </Stack>
      </Card>
    </div>
  );

  return (
    <>
      {row}
      {/*
        The patterns used to live in a Tooltip over the whole row, which PM found
        confusing: a large panel appeared on hover, unbidden, and covered the rows
        below (PSTUDIO-2991). They are now revealed on demand instead.
      */}
      {hasPatterns && expanded && (
        <Card
          test-id={`ps-data-privacy-rule-patterns-${rule.id}`}
          size="medium"
          variant="secondary"
          border="default"
          shadow="none"
          style={{ width: '100%' }}
        >
          <Stack direction="column" gutter="2xsmall" alignItems="stretch">
            {rule.patterns.map((pattern, index) => (
              <TextBody
                key={index}
                size="small"
                // Regexes are long and have no spaces, so they must be allowed to
                // wrap mid-token or they force the card to scroll sideways.
                style={{ wordBreak: 'break-all', fontFamily: 'monospace' }}
              >
                {/* The direction lives on this inner span, not on the TextBody:
                    `dir` on the ps-text-body host did not reach the text. */}
                <MaskingPatternText pattern={pattern} />
              </TextBody>
            ))}
            {brokenPatterns.length > 0 && (
              <TextBody
                test-id={`ps-data-privacy-rule-broken-help-${rule.id}`}
                size="small"
                // `variant` on ps-text-body is primary/secondary/tertiary only —
                // there is no error colour to ask for, and asking would be
                // ignored silently. The red signal is the Tag on the row; this is
                // its explanation, styled like the extra-patterns warning above.
                variant="secondary"
              >
                {String(t('pro-studio/common:maskingRules.brokenPattern'))}
              </TextBody>
            )}
          </Stack>
        </Card>
      )}
      {editing && (
        <RuleEditor
          groupId={groupId}
          groupName={groupName}
          rule={rule}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  );
};

/**
 * The rule set's name, editable in place: click the title and it becomes an
 * input (PSTUDIO-2991).
 *
 * Renaming reuses `updatePypeRuleGroup` with no rule arrays — the backend
 * requires `name` on every update anyway, so a name-only call is a plain rename.
 * Commit on Enter or blur, abandon on Escape; an empty or unchanged name is a
 * no-op rather than an error, since the input is one stray click away.
 */
const RuleSetName: FC<{ ruleSet: RuleSet }> = ({ ruleSet }) => {
  const [t] = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(ruleSet.name);
  // The same signal the Create form and the rule editor give for this input. A
  // name that holds an invisible character keeps the field OPEN and says why,
  // instead of closing it on the old name as if the rename had been saved.
  const draftHasInvisibleCharacters = containsInvisibleCharacters(draft.trim());
  const isViewer = useProStudioViewerRole();
  /**
   * Guards against the edit being settled twice. Leaving edit mode unmounts the
   * input, which fires `onBlur` — so Enter would commit, then blur would commit
   * the same value again (two identical mutations), and Escape's `setEditing(false)`
   * would blur into a commit of the very edit it just abandoned (the blur handler
   * closes over the pre-reset `draft`). A ref, not state, because both handlers
   * run before the next render.
   */
  const settledRef = useRef(false);

  const startEditing = () => {
    settledRef.current = false;
    setDraft(ruleSet.name);
    setEditing(true);
  };

  const commit = (gesture: 'enter' | 'blur') => {
    if (settledRef.current) {
      return;
    }
    // The decision is `decideRuleSetRename`, apart from this component so it can
    // be tested: an unchanged or blank name closes without a write, a name with an
    // invisible character is never written (Enter holds the field open with the
    // message below, blur abandons), anything else is a rename (PSTUDIO-4492,
    // item 5 — the third entrance of the same name).
    const decision = decideRuleSetRename({
      draft,
      currentName: ruleSet.name,
      gesture,
    });
    if (decision.action === 'hold') {
      return;
    }
    settledRef.current = true;
    setEditing(false);
    if (decision.rename === undefined) {
      return;
    }
    sendProStudioEvent({
      type: 'updatePypeRuleGroup',
      payload: { id: ruleSet.id, name: decision.rename },
    });
  };

  const abandon = () => {
    settledRef.current = true;
    setDraft(ruleSet.name);
    setEditing(false);
  };

  // A viewer gets the plain title: no click-to-rename target and no pencil.
  if (isViewer) {
    return (
      <TextTitle
        test-id={`ps-data-privacy-rule-set-name-${ruleSet.id}`}
        size="xsmall"
      >
        {ruleSet.name}
      </TextTitle>
    );
  }

  if (editing) {
    return (
      // The keydown handler sits on this wrapper, NOT on <Input>. The design-system
      // Input handles `keydown` inside its shadow root and never re-emits it, and
      // `@lit/react` only forwards declared events — an `onKeyDown` on the Input
      // would be set as a property and silently never fire. Keyboard events are
      // `composed`, so they bubble out of the shadow root and React's listener on
      // this plain element does see them.
      <span
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            commit('enter');
          }
          if (e.key === 'Escape') {
            abandon();
          }
        }}
      >
        <Input
          test-id={`ps-data-privacy-rule-set-name-input-${ruleSet.id}`}
          className="c-rule-set-name-input"
          // `standard` (the DS default) is the underlined field from the design:
          // a gray bottom border, darker once it has a value, with a purple accent
          // line on hover/focus. `outlined` would draw a full boxed border.
          variant="standard"
          value={draft}
          hasError={draftHasInvisibleCharacters}
          helpText={
            draftHasInvisibleCharacters
              ? String(t('pro-studio/common:maskingRules.invisibleCharacters'))
              : ''
          }
          onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
          onBlur={() => commit('blur')}
        />
      </span>
    );
  }

  return (
    <Stack alignItems="center" gutter="2xsmall" nowrap>
      {/* TextTitle takes no onClick (it is a web component wrapper), so the
          click target is this plain span. */}
      <span
        role="button"
        tabIndex={0}
        style={{ cursor: 'pointer' }}
        onClick={startEditing}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            startEditing();
          }
        }}
      >
        <TextTitle
          test-id={`ps-data-privacy-rule-set-name-${ruleSet.id}`}
          size="xsmall"
        >
          {ruleSet.name}
        </TextTitle>
      </span>
      <Tooltip content="Rename rule set">
        <IconButton
          test-id={`ps-data-privacy-rule-set-rename-${ruleSet.id}`}
          name="pencil"
          size="small"
          onClick={startEditing}
        />
      </Tooltip>
    </Stack>
  );
};

const RuleSetCard: FC<{
  ruleSet: RuleSet;
  channels: Channel[];
  onRequestDelete: (ruleSet: RuleSet) => void;
}> = ({ ruleSet, channels, onRequestDelete }) => {
  const [addingRule, setAddingRule] = useState(false);
  const isViewer = useProStudioViewerRole();
  // The project default is protected: channels with no rule set of their own
  // fall back to it, so deleting it would leave them unmasked (PSTUDIO-3804).
  const isDefault = isDefaultRuleSet(ruleSet);

  // Channels whose pype+env is bound to this rule set. group.pypes env is
  // 'Live'/'Sandbox'; channel.env is lower-case — compare case-insensitively.
  const assignedChannels = channels.filter((channel) =>
    ruleSet.pypes.some(
      (pype) =>
        pype.pypeId === channel.pypeId &&
        pype.env.toLowerCase() === channel.env.toLowerCase()
    )
  );

  return (
    <Card size="large" variant="secondary" border="default" shadow="none">
      <Stack alignItems="center" justifyContent="space-between" nowrap>
        <Stack alignItems="center" gutter="xsmall" nowrap>
          <RuleSetName ruleSet={ruleSet} />
          {isDefault && (
            <Tooltip content="Applied to any channel with no rule set of its own. This rule set can't be deleted.">
              <Tag
                test-id="ps-data-privacy-default-rule-set-tag"
                size="small"
                minimal
              >
                Default
              </Tag>
            </Tooltip>
          )}
        </Stack>
        {!isViewer && !isDefault && (
          <IconButton
            test-id={`ps-data-privacy-rule-set-delete-${ruleSet.id}`}
            name="delete"
            size="small"
            onClick={() => onRequestDelete(ruleSet)}
          />
        )}
      </Stack>

      <Spacer size="medium" />
      <Stack direction="column" gutter="small" alignItems="stretch">
        {ruleSet.rules.map((rule) => (
          <RuleRow
            key={rule.id}
            groupId={ruleSet.id}
            groupName={ruleSet.name}
            rule={rule}
          />
        ))}
        {addingRule && (
          <RuleEditor
            groupId={ruleSet.id}
            groupName={ruleSet.name}
            onClose={() => setAddingRule(false)}
          />
        )}
      </Stack>

      <Spacer size="medium" />
      {!isViewer && (
        <Button
          size="small"
          variant="ghost"
          disabled={addingRule}
          onClick={() => setAddingRule(true)}
        >
          <Icon
            name="plus"
            size="small"
            variant="filled"
            slot="prefix"
            style={{
              ['--icon-bg-color' as string]: 'var(--purple-300, #8573e7)',
              ['--icon-color' as string]: '#fff',
            }}
          />
          Add Rule
        </Button>
      )}

      <ChannelsUsingIt channels={assignedChannels} />
    </Card>
  );
};

const CreateRuleSetCard: FC<{ onClose: () => void }> = ({ onClose }) => {
  const [t] = useTranslation();
  const [name, setName] = useState('');
  // The same definition as the rule name in `RuleEditor`: `trim()` leaves a
  // zero-width space in place, so a set named with one would be saved and render
  // blank (PSTUDIO-4492, item 5).
  const nameHasInvisibleCharacters = containsInvisibleCharacters(name.trim());

  const handleCreate = () => {
    sendProStudioEvent({
      type: 'createPypeRuleGroup',
      payload: { name: name.trim(), cb: onClose },
    });
  };

  return (
    <Card size="large" variant="secondary" border="default" shadow="none">
      <TextBody size="small">Rule Set Name</TextBody>
      <Spacer size="2xsmall" />
      <Stack alignItems="center" gutter="xsmall" nowrap>
        <Input
          variant="outlined"
          value={name}
          hasError={nameHasInvisibleCharacters}
          helpText={
            nameHasInvisibleCharacters
              ? String(t('pro-studio/common:maskingRules.invisibleCharacters'))
              : ''
          }
          onChange={(e) => setName((e.target as HTMLInputElement).value)}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
        />
        <Button size="small" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="small"
          disabled={!isUsableRuleName(name)}
          onClick={handleCreate}
        >
          Create
        </Button>
      </Stack>
    </Card>
  );
};

export const DataPrivacy: FC = () => {
  const [creatingSet, setCreatingSet] = useState(false);
  const isViewer = useProStudioViewerRole();

  const { pypeRuleGroups, channels } = useProStudioCtxSelector((ctx) => ({
    pypeRuleGroups: ctx.pypeRuleGroups,
    channels: ctx.pypeRuleGroupChannels,
  }));

  const { openModal: openDeleteRuleSetModal, deleteRuleSetModal } =
    useDeleteRuleSetModal();

  // WithLoadingState mounts this page before the machine reaches `idle` (it
  // only hides it via CSS while loading), so gate the load dispatch on
  // `useLoadingState()` — it (re)fires once the machine is ready — and use
  // `hasLoaded` to avoid re-dispatching after the first successful load.
  const isPageLoading = useLoadingState();
  const hasLoaded = pypeRuleGroups != null;
  useEffect(() => {
    if (!isPageLoading && !hasLoaded) {
      sendProStudioEvent({ type: 'loadPypeRuleGroups' });
    }
  }, [isPageLoading, hasLoaded]);

  return (
    <PageSection size="small" test-id="ps-data-privacy-section">
      <Stack alignItems="center" justifyContent="space-between" nowrap>
        <Stack alignItems="center" gutter="2xsmall" nowrap>
          <TextTitle size="small">Data Privacy</TextTitle>
          <Tooltip placement="right">
            <Icon name="info" color="gray" size="small" />
            <TextBody slot="content" size="small">
              Use regular expression matching to mask sensitive information when
              displayed to your agents, admins, customers and in chat logs.
            </TextBody>
          </Tooltip>
        </Stack>
        {!isViewer && (
          <Button
            test-id="ps-data-privacy-create-rule-set"
            size="large"
            // Also disabled until the list has loaded: the create card would
            // otherwise render alongside the loading panel, and `createPypeRuleGroup`
            // is only handled once the machine is idle anyway.
            disabled={creatingSet || !hasLoaded}
            onClick={() => setCreatingSet(true)}
          >
            <Icon name="add" slot="prefix" />
            Create Rule Set
          </Button>
        )}
      </Stack>

      <Spacer size="xlarge" />

      {/*
        Wrapper is load-bearing: `deleteRuleSetModal` below portals itself out
        while open, so mounting/unmounting a sibling at this position throws in the
        DOM. See portaled-modal-sibling.test.tsx.
      */}
      <div>
        {!hasLoaded ? (
          <SettingsSectionLoader testId="ps-data-privacy-loading" />
        ) : (
          !creatingSet &&
          (pypeRuleGroups ?? []).length === 0 && (
            <SettingsEmptyState
              testId="ps-data-privacy-empty"
              title="No rule sets yet"
              description="Create a rule set to mask sensitive values, then assign it to channels from their settings."
            />
          )
        )}
      </div>

      <Stack direction="column" gutter="large" alignItems="stretch">
        {creatingSet && (
          <CreateRuleSetCard onClose={() => setCreatingSet(false)} />
        )}
        {(pypeRuleGroups ?? []).map((ruleSet) => (
          <RuleSetCard
            key={ruleSet.id}
            ruleSet={ruleSet}
            channels={channels ?? []}
            onRequestDelete={(target) =>
              openDeleteRuleSetModal({
                ruleSetId: target.id,
                ruleSetName: target.name,
              })
            }
          />
        ))}
      </Stack>
      {deleteRuleSetModal}
    </PageSection>
  );
};
